import React, { useMemo, useState, useEffect, createContext, useContext } from 'react';
import MiscUtils from 'services/MiscUtils';
import PreviewDocumentModal from 'components/Modals/PreviewDocumentModal';
import Icon from 'components/Utils/NewIcon';
import DefaultModal from 'components/Utils/DefaultModal';
import DocumentUtils from 'services/DocumentUtils';
import CPPUtils from 'services/CPPUtils';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import { uiActions } from 'careand-redux/slices/ui/ui';
import { Alert } from 'components/HealthRecords/Documents/DocumentBadge';
import DocumentDetailsModal from 'components/HealthRecords/Documents/DocumentDetailsModal';
import { ActivePhysician } from 'careand-redux/selectors';
import { FloatTopCPPIcon } from 'components/Tasks/Utils/CheckIcon';
import ConfirmationIcon from 'components/Utils/NewIcon/ConfirmationIcon';
import { setFormValues } from 'hooks/useReduxForm';
import { Physician } from '../../../../../generated/api';

const ACTIVE_TAB = 'referral';

export const EditDocumentModalContext = createContext<{
  documentId: string | undefined;
  patientId: string | undefined;
  setModalDocument: (id: string | undefined) => void;
}>({
  documentId: undefined,
  patientId: undefined,
  setModalDocument: (id) => {},
});

interface CPPTimelineDocumentProps {
  document: any;
  patient: any;
  setActiveCPPTab: (tab: string) => void;
  index?: number;
}

const CPPTimelineDocument = (props: CPPTimelineDocumentProps) => {
  const { document, setActiveCPPTab, index } = props;
  const dispatch = useAppDispatch();
  const { setModalDocument } = useContext(EditDocumentModalContext);
  let ownPhysician = useAppSelector(ActivePhysician);
  const physicians = useAppSelector((state) => state.physicians.byId);
  const admins = useAppSelector((state) => state.admins.byId);

  const [deletingDocument, setDeletingDocument] = useState(false);
  const [thumbnail, setThumbnail] = useState<string | undefined>();
  const [floating, setFloating] = useState<boolean | undefined>();

  const isPending = document.pending_patient_fill ?? false;

  const flagsText = useMemo(
    () =>
      DocumentUtils.getFlagsAndCommentsAlert(document, undefined, undefined, physicians, admins),
    [document, physicians, admins],
  );

  useEffect(() => {
    if (document.thumbnail && !thumbnail) {
      dispatch(api.Resource.getResource({ resource: { url: document.thumbnail } })).then(
        (result: any) => {
          if (result) {
            let blobUrl = MiscUtils.getBlobUrl(result);
            setThumbnail(blobUrl);
          }
        },
      );
    }
  }, [dispatch, document.thumbnail, index, thumbnail]);

  async function deleteDocumentWrapper() {
    setDeletingDocument(true);
    await dispatch(api.Documents.deleteDocument({ documentId: document.id }), {
      errorTitle: 'Delete Document',
    });
    setDeletingDocument(false);
  }

  function openEditModal() {
    setModalDocument(document.id);
  }

  function onClickedPin(event: React.MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    dispatch(
      uiActions.addPinnedDocument({
        id: document.id,
        type: 'document',
        patientId: document.patient_id,
      }),
    );
  }

  async function onClickedFloat(event: React.MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    setFloating(true);
    await dispatch(
      api.Documents.updateDocument({ patientDocument: { ...document, pinned: !document.pinned } }),
    );
    setFloating(false);
  }

  const { title, document_type, hide, approved, author_id, physician_id } = document;

  let description = `${title}`;
  if (hide) description += ` (hidden)`;

  let rootClassName = hide || approved === false ? 'greyed-out' : '';

  let isAuthorized =
    !ownPhysician!.intern_of || author_id === ownPhysician!.id || physician_id === ownPhysician!.id;

  const icon = document.has_repeat ? 'repeat' : 'file-medical';

  const alert = document.fee && !document.paid;

  return (
    <>
      <div className="item-wrapper clickable" onClick={openEditModal}>
        <div className={rootClassName}>
          <div className="hover-icons">
            {isAuthorized && !!document.referral && (
              <EditReferral
                document={document}
                patient={props.patient}
                setActiveCPPTab={setActiveCPPTab}
              />
            )}
            <FloatTopCPPIcon float={document.pinned} onClick={onClickedFloat} loading={floating} />
            <Icon
              src="thumbtack"
              showTooltip={true}
              height="20px"
              width="20px"
              alt="Pin to Side"
              className="pin"
              onClick={onClickedPin}
            />
            {isAuthorized && (
              <ConfirmationIcon
                src="trash"
                showTooltip={true}
                height="20px"
                width="20px"
                alt="Delete"
                className="trash"
                confirmText="This will delete the document. Proceed?"
                loading={deletingDocument}
                onClick={deleteDocumentWrapper}
              />
            )}
          </div>
          <div className="top-bar">
            {alert ? (
              <Alert
                key="fee-alert"
                text={`Unpaid fee: $${document.fee}`}
                icon="circle-dollar"
                //@ts-ignore
                height="15px"
                width="15px"
                style={{ color: '#d16f3e' }}
              />
            ) : isPending ? (
              <Alert
                key="fee-alert"
                text="Pending patient fill"
                icon="user-pen"
                //@ts-ignore
                height="15px"
                width="15px"
                style={{ color: '#d16f3e' }}
              />
            ) : (
              <Icon src={icon} height="15px" width="15px" />
            )}
            <span className="small-title">{document_type || 'Document'}</span>
          </div>
          <div className="item-content">
            <div className="block">
              {approved === false && <span className="bold block">[NOT APPROVED]</span>}
              <span>{description}</span>
              {document.has_repeat && (
                <span className="block">
                  Repeats every {document.repeat_options?.number} {document.repeat_options?.unit}
                </span>
              )}
            </div>
            {!!flagsText && (
              <>
                <br />
                <pre className="inline-block">{flagsText}</pre>
              </>
            )}
            {!!thumbnail && (
              <div className="thumbnail">
                <img src={thumbnail} />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

interface EditReferralProps {
  document: any;
  patient: any;
  setActiveCPPTab: (tab: string) => void;
}

const EditReferral = ({ document, patient, setActiveCPPTab }: EditReferralProps) => {
  const dispatch = useAppDispatch();

  function onCreateReferral(event: React.MouseEvent) {
    event.stopPropagation();
    if (!!document.referral) {
      let data = document.referral;
      setActiveCPPTab(ACTIVE_TAB);
      const formName = CPPUtils.getFormName(ACTIVE_TAB, document.patient_id);
      dispatch(setFormValues({ formName, values: { ...data } }));
    }
  }

  return (
    <Icon
      src="file-plus"
      alt="Create New Referral"
      height="20px"
      showTooltip={true}
      className="icon-create-document mr-1"
      onClick={onCreateReferral}
    />
  );
};

export const EditDocumentModal = () => {
  const { documentId, setModalDocument, patientId } = useContext(EditDocumentModalContext);
  const document = useAppSelector(
    (state) =>
      state.healthRecords.byId[patientId!]?.documents.find((it: any) => it.id === documentId),
  );

  const ownPhysician = useAppSelector(
    (state) => state.physicians.byId[state.authentication.user!.id!] || state.authentication.user!,
  )!;
  const isAuthorized =
    !(ownPhysician as Physician).intern_of ||
    (document as any)?.author_id === ownPhysician.id ||
    document?.physician_id === ownPhysician.id;

  if (!document) return null;

  function closeEditModal() {
    setModalDocument(undefined);
  }

  return (
    document && (
      <DocumentDetailsModal
        document={document}
        onClose={closeEditModal}
        isOpen={!!document}
        title="Document Details"
        allowPin={true}
        onEdit={closeEditModal}
        disablePatientBadge={true}
        disableEdit={!isAuthorized}
      />
    )
  );
};

export const MediaPreviewModal = ({
  title,
  setOpenPreview,
  openPreview,
  patient,
  url,
  type = 'application/pdf',
  faxOptions,
}: {
  title: string;
  setOpenPreview: (open: boolean) => void;
  openPreview: boolean;
  patient: any;
  url?: string;
  type?: string;
  faxOptions?: any;
}) => {
  function closePreviewModal() {
    setOpenPreview(false);
  }

  return (
    openPreview && (
      <DefaultModal
        className="large-modal"
        key="preview-document-modal"
        content={
          //@ts-ignore
          <PreviewDocumentModal
            showFax={true}
            onClose={closePreviewModal}
            type={type}
            url={url}
            title={title}
            patient={patient}
            faxOptions={faxOptions}
          />
        }
        isOpen={openPreview}
        onRequestClose={closePreviewModal}
      />
    )
  );
};

export default CPPTimelineDocument;
